import React from 'react';
import { Mic, Check, DollarSignIcon, WifiIcon, SparklesIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { useLanguage } from '../hooks/useLanguage';
export function HeroSection() {
  const {
    t
  } = useLanguage();
  return <section className="relative min-h-screen flex items-center justify-center py-20 px-4 overflow-hidden bg-gradient-to-br from-[#0F0B2E] via-[#1E1B4B] to-[#312E81]" data-id="element-86">
      {/* Background Effects */}
      <div className="absolute inset-0 z-0" data-id="element-87">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-[120px] opacity-50" data-id="element-88"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[100px] opacity-40" data-id="element-89"></div>
        <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/wd9aG4HjjV3GDnmdaJgKai/Screenshot_2026-04-16_12.01.54_PM.png')] bg-cover bg-center opacity-10 mix-blend-overlay" data-id="element-90"></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-91">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-brand-amber font-bold uppercase tracking-wider text-sm mb-8 animate-fade-in-up" data-id="element-92">
          <Mic className="w-4 h-4" data-id="element-93" />
          <span data-id="element-94">{t('hero', 'badge')}</span>
        </div>

        <h1 className="font-heading font-bold text-white mb-6 leading-tight uppercase tracking-tight drop-shadow-2xl animate-fade-in-up [animation-delay:200ms]" data-id="element-95">
          <span className="block text-2xl md:text-3xl lg:text-4xl text-blue-200 mb-2" data-id="element-96">
            {t('hero', 'title1')}
          </span>
          <span className="block text-5xl md:text-7xl lg:text-8xl text-transparent bg-clip-text bg-gradient-to-r from-brand-amber to-cyan-300" data-id="element-97">
            {t('hero', 'title2')}
          </span>
        </h1>

        <p className="text-xl md:text-2xl text-blue-100 mb-6 max-w-2xl mx-auto font-light leading-relaxed animate-fade-in-up [animation-delay:400ms]" data-id="element-98">
          {t('hero', 'subtitle')}
        </p>

        {/* Highlight Banner */}
        <div className="relative inline-block mb-10 animate-fade-in-up [animation-delay:450ms]" data-id="element-99">
          <div className="absolute -inset-1 bg-gradient-to-r from-brand-amber via-cyan-400 to-brand-gold rounded-2xl blur-md opacity-30 animate-pulse" data-id="element-100"></div>
          <div className="relative flex items-center gap-3 px-8 py-5 rounded-2xl bg-[#1E1B4B]/80 backdrop-blur-sm border border-brand-amber/30" data-id="element-101">
            <SparklesIcon className="w-7 h-7 text-brand-amber shrink-0" data-id="element-102" />
            <span className="text-lg md:text-xl font-bold leading-snug text-left" data-id="element-103">
              <span className="text-white" data-id="element-104">{t('hero', 'highlight1')}</span>
              <span className="text-brand-amber font-extrabold" data-id="element-105">
                {t('hero', 'highlight2')}
              </span>
              <br data-id="element-106" />
              <span className="text-white font-semibold text-base" data-id="element-107">
                {t('hero', 'highlight3')}
              </span>
            </span>
          </div>
        </div>

        {/* Product Image */}
        <div className="mb-10 animate-fade-in-up [animation-delay:500ms]" data-id="element-108">
          <img src={t('hero', 'productImage')} alt="Guía para entrar al mundo del doblaje de voz" className="mx-auto max-w-sm md:max-w-md lg:max-w-lg w-full h-auto rounded-xl shadow-2xl shadow-black/40 border border-white/10" data-id="element-109" />
        </div>

        <div className="flex flex-col items-center gap-4 animate-fade-in-up [animation-delay:600ms]" data-id="element-110">
          <a href={t('hero', 'ctaLink')} target="_blank" rel="noopener noreferrer" data-id="element-111">
            <Button variant="primary" size="xl" glow withIcon data-id="element-112">
              {t('hero', 'cta')}
            </Button>
          </a>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-blue-200 mt-2" data-id="element-113">
            <span className="flex items-center gap-1" data-id="element-114">
              <Check className="w-4 h-4 text-brand-amber" data-id="element-115" />{' '}
              {t('hero', 'check1')}
            </span>
            <span className="flex items-center gap-1" data-id="element-116">
              <Check className="w-4 h-4 text-brand-amber" data-id="element-117" />{' '}
              {t('hero', 'check2')}
            </span>
            <span className="flex items-center gap-1" data-id="element-118">
              <Check className="w-4 h-4 text-brand-amber" data-id="element-119" />{' '}
              {t('hero', 'check3')}
            </span>
          </div>

          {/* Persuasive Sales Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6 w-full max-w-2xl" data-id="element-120">
            {/* Badge 1 — Earnings */}
            <div className="flex items-center gap-4 bg-gradient-to-r from-brand-amber to-brand-gold rounded-2xl px-6 py-5 shadow-xl shadow-brand-amber/30 border-2 border-pink-300/50" data-id="element-121">
              <div className="shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-122">
                <DollarSignIcon className="w-7 h-7 text-white" data-id="element-123" />
              </div>
              <div className="text-left" data-id="element-124">
                <p className="text-white font-heading font-bold text-lg leading-tight uppercase tracking-wide" data-id="element-125">
                  {t('hero', 'badge1Title')}
                </p>
                <p className="text-purple-200 font-bold text-sm" data-id="element-126">
                  {t('hero', 'badge1Sub')}
                </p>
              </div>
            </div>

            {/* Badge 2 — Remote Work */}
            <div className="flex items-center gap-4 bg-gradient-to-r from-white/15 to-white/10 backdrop-blur-sm rounded-2xl px-6 py-5 shadow-xl border-2 border-white/30" data-id="element-127">
              <div className="shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center" data-id="element-128">
                <WifiIcon className="w-7 h-7 text-white" data-id="element-129" />
              </div>
              <div className="text-left" data-id="element-130">
                <p className="text-white font-heading font-bold text-lg leading-tight uppercase tracking-wide" data-id="element-131">
                  {t('hero', 'badge2Title')}
                </p>
                <p className="text-blue-200 font-semibold text-sm" data-id="element-132">
                  {t('hero', 'badge2Sub')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}